import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

import SEO from "./components/common/SEO";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Toaster from "./components/ui/Toaster";
import ScrollToTop from "./components/ui/ScrollToTop";

import FeedbackWidget from "./components/common/FeedbackWidget";
import { PostHogProvider } from 'posthog-js/react';
import PostHogPageview from "./components/common/PostHogPageview";

// Pages / Sections
import Hero from "./components/Hero";
import Analytics from "./components/Analytics";
import Newsletter from "./components/Newsletter";
import Cards from "./components/Cards";
import FAQ from "./components/FAQ";
import MachineLearning from "./components/MachineLearning";
import FoundationCourse from "./components/courses/FoundationCourse";
import About from "./components/About";
import LoginForm from "./components/LoginForm";
import RegisterForm from "./components/RegisterForm";
import ForgotPassword from "./components/ForgotPassword";
import UpdatePassword from "./components/UpdatePassword";
import Account from "./components/Account";
import SQLPlayground from "./components/SQLPlayground";
import DataSandbox from "./components/DataSandbox";
import ThinkingInData from "./components/exercises/ThinkingInData";
import SQLCheatsheet from "./components/SQLCheatsheet";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import TermsOfService from "./components/pages/TermsOfService";
import Contact from "./components/pages/Contact";
import FAQPage from "./components/pages/FAQPage";
import BlogIndex from "./components/pages/BlogIndex";
import BlogPost from "./components/pages/BlogPost";
import DataVizModule from "./components/exercises/DataVizModule";
import DataVizIndex from "./components/exercises/DataVizIndex";
import PracticeDashboard from "./components/exercises/PracticeDashboard";

// 🔐 Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a1220] to-[#1e3a5f] text-white">
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  return children;
};

// Helper to handle intelligent redirect after login
const LoginRedirect = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null; // or a spinner

  if (user) {
    // If user is already logged in, redirect them.
    // 1. Check if there's a specific "from" location in state (from ProtectedRoute)
    // 2. Otherwise default to Dashboard (Thinking in Data)
    const from = location.state?.from || "/exercises";
    return <Navigate to={from} replace />;
  }

  return children;
};

function App() {
  const { loading } = useAuth();
  const location = useLocation();
  const isDataViz = location.pathname.startsWith('/exercises/data-viz-') ||
                     location.pathname.includes('/exercises/thinking-in-data/case/');

  const options = {
    api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
    autocapture: true,
    capture_pageview: false, // Managed by PostHogPageview component
    capture_pageleave: false
  }

  console.log('PostHog Init:', {
    key: process.env.REACT_APP_POSTHOG_KEY ? 'Present' : 'Missing',
    host: options.api_host,
    autocapture: options.autocapture
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a1220] to-[#1e3a5f] text-white">
        Loading...
      </div>
    );
  }

  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_KEY}
      options={options}
    >
      <HelmetProvider>
        <ThemeProvider>
          <Toaster />
          <ScrollToTop />
          <FeedbackWidget />
          {!isDataViz && <Navbar />}

          <div className={`min-h-screen flex flex-col bg-background-DEFAULT dark:bg-background-darker transition-colors duration-300 ${isDataViz ? 'h-screen overflow-hidden' : ''}`}>
            <div className={`flex-grow ${isDataViz ? 'h-full' : ''}`}>
              <PostHogPageview />
              <Routes>
                {/* Home */}
                <Route
                  path="/"
                  element={
                    <>
                      <SEO
                        title="DataHelix | Master Data Analytics with SQL"
                        description="The interactive platform for Data Analysts. Learn SQL by solving real-world business cases, not just syntax."
                      />
                      <Hero />
                      <Analytics />
                      <Newsletter />
                      <Cards />
                      <FAQ />
                    </>
                  }
                />
                <Route path="/privacy-policy" element={
                  <>
                    <SEO title="Privacy Policy | DataHelix" description="Read our Privacy Policy to understand how we collect, use, and protect your data." />
                    <PrivacyPolicy />
                  </>
                } />
                <Route path="/terms-of-service" element={
                  <>
                    <SEO title="Terms of Service | DataHelix" description="Our terms and conditions for using the DataHelix platform." />
                    <TermsOfService />
                  </>
                } />
                <Route path="/contact" element={
                  <>
                    <SEO title="Contact Us | DataHelix" description="Get in touch with the DataHelix team." />
                    <Contact />
                  </>
                } />
                <Route path="/faq" element={
                  <>
                    <SEO title="FAQ | DataHelix" description="Frequently Asked Questions about DataHelix." />
                    <FAQPage />
                  </>
                } />
                <Route path="/resources/blog" element={<BlogIndex />} />
                <Route path="/resources/blog/:slug" element={<BlogPost />} />
                <Route
                  path="/login"
                  element={
                    <LoginRedirect>
                      <LoginForm />
                    </LoginRedirect>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <LoginRedirect>
                      <RegisterForm />
                    </LoginRedirect>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <LoginRedirect>
                      <ForgotPassword />
                    </LoginRedirect>
                  }
                />
                <Route
                  path="/update-password"
                  element={<UpdatePassword />}
                />

                {/* Protected Pages */}
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <Account />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/machine-learning"
                  element={
                    <ProtectedRoute>
                      <MachineLearning />
                    </ProtectedRoute>
                  }
                />

                <Route path="/resources/sql-cheatsheet" element={<ProtectedRoute><SQLCheatsheet /></ProtectedRoute>} />

                <Route
                  path="/courses/foundations"
                  element={
                    <ProtectedRoute>
                      <FoundationCourse />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/sql-playground"
                  element={
                    <ProtectedRoute>
                      <SQLPlayground />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/data-sandbox"
                  element={
                    <ProtectedRoute>
                      <SEO title="Data Sandbox | DataHelix" description="Upload your own CSV files and query them instantly in your browser with DuckDB." />
                      <DataSandbox />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/exercises"
                  element={<PracticeDashboard />}
                />

                <Route
                  path="/exercises/thinking-in-data/case/:caseIndex/challenge/:challengeIndex"
                  element={
                    <ProtectedRoute>
                      <ThinkingInData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exercises/thinking-in-data/case/:caseIndex"
                  element={
                    <ProtectedRoute>
                      <ThinkingInData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exercises/thinking-in-data"
                  element={
                    <>
                      <SEO title="Thinking in Data | Curriculum" description="Interactive SQL curriculum designed for data analysts." />
                      <ThinkingInData />
                    </>
                  }
                />

                <Route
                  path="/exercises/data-viz"
                  element={<DataVizIndex />}
                />

                <Route
                  path="/exercises/data-viz-intro"
                  element={
                    <ProtectedRoute>
                      <SEO title="Data Visualization Intro" description="Learn the five core chart types - Bar, Line, Pie, Stacked Bar, and Area - with a real video streaming dataset." />
                      <DataVizModule moduleId="DATA_VIZ_INTRO" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/exercises/data-viz-intermediate"
                  element={
                    <ProtectedRoute>
                      <SEO title="Data Visualization Intermediate" description="Master advanced chart types - Histogram, Heatmap, Map, Box Plot, and Scatter - with a real e-commerce dataset." />
                      <DataVizModule moduleId="DATA_VIZ_INTERMEDIATE" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/exercises/data-viz-1"
                  element={
                    <ProtectedRoute>
                      <SEO title="Data Visualization | DataHelix" description="Build charts from real app data using an interactive visual query builder." />
                      <DataVizModule moduleId="DATA_VIZ_1" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/exercises/data-viz-2"
                  element={
                    <ProtectedRoute>
                      <SEO title="Customer Financial Behavior | Data Visualization" description="Investigate customer financial behavior and demographics using visual queries." />
                      <DataVizModule moduleId="DATA_VIZ_2" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/exercises/data-viz-3"
                  element={
                    <ProtectedRoute>
                      <SEO title="Ride-Sharing Platform Operations | Data Visualization" description="Investigate ride demand, revenue quality, and driver performance across six cities to identify expansion opportunities." />
                      <DataVizModule moduleId="DATA_VIZ_3" />
                    </ProtectedRoute>
                  }
                />

                {/* Public Pages */}
                <Route path="/about" element={
                  <>
                    <SEO title="About Us | DataHelix" description="Learn about the team building the future of data analytics education." />
                    <About />
                  </>
                } />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/contact" element={<Contact />} />

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>

            {!isDataViz && <Footer />}
          </div>
        </ThemeProvider>
        <VercelAnalytics />
      </HelmetProvider>
    </PostHogProvider>
  );
}

export default App;
